import { v4 as uuidv4 } from 'uuid';
import logService from '../services/logService';

const SENSITIVE_FIELDS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'authorization',
  'authorizationToken',
  'secret',
  'apiKey',
  'apikey',
  'authToken',
  'bearer'
];

/**
 * Sanitiza objetos para remover información sensible
 * @param {any} obj - Objeto a sanitizar
 * @param {number} depth - Profundidad actual (para evitar recursión infinita)
 * @returns {any} - Objeto sanitizado
 */
const sanitizeData = (obj, depth = 0) => {
  if (depth > 10) {
    return '[Object too deep]';
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    if (obj.length > 1000) {
      return obj.substring(0, 1000) + '...[truncated]';
    }
    return obj;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeData(item, depth + 1));
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    
    if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field))) {
      sanitized[key] = '[REDACTED]';
    } else {
      sanitized[key] = sanitizeData(value, depth + 1);
    }
  }

  return sanitized;
};

/**
 * Obtiene el userId del localStorage
 * @returns {string|null} - ID del usuario o null
 */
const getUserId = () => {
  try {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo) {
      return null;
    }
    
    const user = JSON.parse(userInfo);
    return user.id || user.cognito_id || null;
  } catch (error) {
    return null;
  }
};

/**
 * Obtiene el nombre del servicio/componente desde el stack trace
 * @param {string} serviceName - Nombre del servicio pasado como parámetro (opcional)
 * @returns {string} - Nombre del servicio
 */
const getServiceName = (serviceName) => {
  // Si se pasa explícitamente, usarlo
  if (serviceName) {
    return serviceName;
  }

  // Intentar obtener del stack trace
  try {
    const stack = new Error().stack;
    if (stack) {
      const stackLines = stack.split('\n');
      // Buscar la línea que contiene el componente (normalmente línea 3 o 4)
      for (let i = 2; i < Math.min(stackLines.length, 6); i++) {
        const line = stackLines[i];
        // Buscar patrones comunes de componentes React
        const match = line.match(/at\s+(\w+)/);
        if (match && match[1] && match[1] !== 'getServiceName' && match[1] !== 'buildLogObject') {
          return match[1];
        }
      }
    }
  } catch (error) {
    // Si falla, continuar
  }

  return 'unknown-service';
};

/**
 * Construye el objeto de log base
 * @param {string} level - Nivel del log (error, warn, info, debug)
 * @param {string} message - Mensaje del log
 * @param {object} data - Datos adicionales (opcional)
 * @param {Error} error - Objeto de error (opcional)
 * @param {string} serviceName - Nombre del servicio/componente (opcional)
 * @returns {object} - Objeto de log estructurado
 */
const buildLogObject = (level, message, data = null, error = null, serviceName = null) => {
  const logObject = {
    level,
    service: getServiceName(serviceName),
    requestId: uuidv4(),
    message
  };

  const userId = getUserId();
  if (userId) {
    logObject.userId = userId;
  }

  if (data !== null && data !== undefined) {
    logObject.data = sanitizeData(data);
    
    if (data.inspectionId) {
      logObject.inspectionId = data.inspectionId;
    }
  }

  if (error) {
    logObject.error = {
      name: error.name || 'Error',
      message: error.message || String(error),
      stack: error.stack || null
    };
  }

  return logObject;
};

/**
 * Logger principal
 */
const logger = {
  /**
   * Log de nivel INFO
   * @param {string} message - Mensaje descriptivo
   * @param {object} data - Datos adicionales (opcional)
   * @param {string} serviceName - Nombre del servicio/componente (opcional)
   */
  info: (message, data = null, serviceName = null) => {
    const logObject = buildLogObject('info', message, data, null, serviceName);
    logService.sendLog(logObject);
  },

  /**
   * Log de nivel WARN
   * @param {string} message - Mensaje descriptivo
   * @param {object} data - Datos adicionales (opcional)
   * @param {string} serviceName - Nombre del servicio/componente (opcional)
   */
  warn: (message, data = null, serviceName = null) => {
    const logObject = buildLogObject('warn', message, data, null, serviceName);
    logService.sendLog(logObject);
  },

  /**
   * Log de nivel ERROR
   * @param {string} message - Mensaje descriptivo
   * @param {Error} error - Objeto de error
   * @param {object} data - Datos adicionales (opcional)
   * @param {string} serviceName - Nombre del servicio/componente (opcional)
   */
  error: (message, error = null, data = null, serviceName = null) => {
    const logObject = buildLogObject('error', message, data, error, serviceName);
    logService.sendLog(logObject);
  },

  /**
   * Log de nivel DEBUG
   * @param {string} message - Mensaje descriptivo
   * @param {object} data - Datos adicionales (opcional)
   * @param {string} serviceName - Nombre del servicio/componente (opcional)
   */
  debug: (message, data = null, serviceName = null) => {
    const logObject = buildLogObject('debug', message, data, null, serviceName);
    logService.sendLog(logObject);
  }
};

export default logger;